import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Users, TrendingUp, MapPin, Briefcase } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';

const COLORS_GENDER = ['#1F4E79', '#E8593C', '#1D9E75', '#EF9F27', '#378ADD'];
const COLORS_AGE = ['#E6F1FB', '#B5D4F4', '#85B7EB', '#378ADD', '#185FA5', '#0C447C', '#0D2C4E', '#1F4E79', '#2d6ea0', '#3a8dc0'];
const COLORS_MUNI = ['#1F4E79', '#378ADD', '#85B7EB', '#B5D4F4', '#E6F1FB', '#0D2C4E', '#0C447C', '#185FA5', '#2d6ea0', '#5ca0d4'];

const LSM_DATA = [
  { level: 'LSM 6', count: 47, pct: 0.2, color: '#B5D4F4' },
  { level: 'LSM 7', count: 3750, pct: 13.3, color: '#378ADD' },
  { level: 'LSM 8', count: 16187, pct: 57.5, color: '#1F4E79' },
  { level: 'LSM 9', count: 8154, pct: 29.0, color: '#0D2C4E' },
];

const INCOME_DATA = [
  { age: '25-29', grade: 'G5-6', min: 180, max: 240, mid: 210, count: 47 },
  { age: '30-34', grade: 'G6-7', min: 220, max: 310, mid: 265, count: 780 },
  { age: '35-39', grade: 'G7-8', min: 290, max: 390, mid: 340, count: 2970 },
  { age: '40-44', grade: 'G8-9', min: 360, max: 480, mid: 420, count: 4850 },
  { age: '45-49', grade: 'G9-10', min: 430, max: 580, mid: 505, count: 5102 },
  { age: '50-54', grade: 'G10-11', min: 500, max: 700, mid: 600, count: 4658 },
  { age: '55-59', grade: 'G11-12', min: 580, max: 820, mid: 700, count: 3496 },
  { age: '60+', grade: 'G12+', min: 650, max: 950, mid: 800, count: 6235 },
];

export const AdminSamwuStatistics = () => {
  const { adminApi } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await adminApi.get('/admin/samwu/stats');
        setStats(res.data);
      } catch (err) {
        console.error('Stats error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [adminApi]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-afriperx-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!stats) return <p className="text-gray-500 text-center py-12">Failed to load statistics</p>;

  const genderData = Object.entries(stats.gender || {}).map(([name, value]) => ({ name: name || 'Unknown', value }));
  const ageData = Object.entries(stats.age_distribution || {}).map(([name, value]) => ({ name, value }));
  const muniData = Object.entries(stats.municipality || {}).map(([name, value]) => ({ name: name || 'Unknown', value }));
  const workplaceData = Object.entries(stats.top_workplaces || {}).map(([name, value]) => ({ name, value }));

  const totalMembers = stats.total_members || 0;

  return (
    <div className="space-y-6" data-testid="admin-samwu-statistics">
      {/* Title */}
      <div>
        <div className="flex items-center gap-3">
          <h1 className="font-syne text-2xl font-bold text-gray-900">Analytics</h1>
          <span className="text-xs font-medium bg-afriperx-green text-white px-3 py-1 rounded-full">LIVE DATA</span>
        </div>
        <p className="text-gray-500 text-sm mt-1">Member Database Intelligence - City of Tshwane & Ekurhuleni</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard value={totalMembers.toLocaleString()} label="Total Members" sub="" color="#1F4E79" icon={Users} />
        <KpiCard
          value={genderData.find(g => g.name === 'Male')?.value?.toLocaleString() || '0'}
          label="Male Members"
          sub={totalMembers ? `${((genderData.find(g => g.name === 'Male')?.value || 0) / totalMembers * 100).toFixed(1)}%` : ''}
          color="#378ADD"
          icon={Users}
        />
        <KpiCard
          value={genderData.find(g => g.name === 'Female')?.value?.toLocaleString() || '0'}
          label="Female Members"
          sub={totalMembers ? `${((genderData.find(g => g.name === 'Female')?.value || 0) / totalMembers * 100).toFixed(1)}%` : ''}
          color="#E8593C"
          icon={Users}
        />
        <KpiCard value={`${stats.average_age || 0} yrs`} label="Average Age" sub="Member base" color="#EF9F27" icon={TrendingUp} />
      </div>

      {/* Charts Row 1: Age + Gender */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Age Distribution */}
        <Card className="bg-white border border-gray-200 p-5 rounded-xl">
          <h3 className="font-syne text-sm font-bold text-gray-900 mb-1">Age Distribution</h3>
          <p className="text-xs text-gray-400 mb-4">Member count by age band</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={ageData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(13,44,78,0.07)" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#6b7280' }} />
              <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v} />
              <Tooltip formatter={(v) => v.toLocaleString()} />
              <Bar dataKey="value" name="Members" radius={[4, 4, 0, 0]}>
                {ageData.map((_, i) => <Cell key={i} fill={COLORS_AGE[i % COLORS_AGE.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Gender Donut */}
        <Card className="bg-white border border-gray-200 p-5 rounded-xl">
          <h3 className="font-syne text-sm font-bold text-gray-900 mb-1">Gender Breakdown</h3>
          <p className="text-xs text-gray-400 mb-4">Male vs Female across all members</p>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={genderData}
                cx="50%" cy="50%"
                innerRadius={55} outerRadius={85}
                paddingAngle={2}
                dataKey="value"
              >
                {genderData.map((_, i) => <Cell key={i} fill={COLORS_GENDER[i % COLORS_GENDER.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => v.toLocaleString()} />
              <Legend iconType="square" formatter={(v) => <span className="text-xs text-gray-600">{v}</span>} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Charts Row 2: Income & LSM */}
      <div className="grid md:grid-cols-5 gap-4">
        {/* Income Table */}
        <Card className="bg-white border border-gray-200 p-5 rounded-xl md:col-span-3">
          <h3 className="font-syne text-sm font-bold text-gray-900 mb-1">Estimated Income by Age Group</h3>
          <p className="text-xs text-gray-400 mb-4">Based on SA government salary grades - municipal workers</p>
          <div className="space-y-2">
            {INCOME_DATA.map((d, i) => (
              <div key={i} className="flex items-center gap-3 py-1.5 border-b border-gray-100 last:border-0">
                <span className="text-xs font-medium text-gray-900 w-12">{d.age}</span>
                <div className="flex-1">
                  <p className="text-[10px] text-gray-400">{d.grade} &middot; R{d.min}K-R{d.max}K</p>
                  <div className="h-1.5 bg-gray-100 rounded-full mt-1">
                    <div className="h-1.5 rounded-full" style={{ width: `${(d.mid / 800) * 100}%`, background: COLORS_AGE[i] }} />
                  </div>
                </div>
                <span className="text-xs text-gray-500 w-16 text-right">R{d.mid}K avg</span>
                <span className="text-[10px] text-gray-400 w-10 text-right">{d.count >= 1000 ? `${(d.count / 1000).toFixed(1)}K` : d.count}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* LSM Profile */}
        <Card className="bg-white border border-gray-200 p-5 rounded-xl md:col-span-2">
          <h3 className="font-syne text-sm font-bold text-gray-900 mb-1">LSM Profile</h3>
          <p className="text-xs text-gray-400 mb-4">Living Standards Measure</p>
          <div className="space-y-3">
            {LSM_DATA.map((d, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-12 font-medium">{d.level}</span>
                <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden">
                  <div
                    className="h-5 rounded flex items-center justify-end px-2 transition-all duration-1000"
                    style={{ width: `${d.pct}%`, background: d.color }}
                  >
                    <span className="text-[10px] font-medium text-white">{d.pct}%</span>
                  </div>
                </div>
                <span className="text-xs text-gray-400 w-14 text-right">{d.count.toLocaleString()}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 bg-gray-50 rounded-lg text-xs text-gray-500 leading-relaxed">
            <strong className="text-gray-700 block mb-1">What this means</strong>
            LSM 8-9 signals upper-middle income earners with home ownership, multiple vehicles, and broad financial product access. Ideal target for insurance, retirement planning, and asset-backed lending.
          </div>
        </Card>
      </div>

      {/* Charts Row 3: Municipality + Wealth */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Municipality */}
        <Card className="bg-white border border-gray-200 p-5 rounded-xl">
          <h3 className="font-syne text-sm font-bold text-gray-900 mb-1">Members by Municipality</h3>
          <p className="text-xs text-gray-400 mb-4">Geographic distribution</p>
          <div className="space-y-3">
            {muniData.map((d, i) => {
              const pct = totalMembers ? ((d.value / totalMembers) * 100).toFixed(1) : 0;
              return (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs text-gray-700 font-medium min-w-[120px] truncate">{d.name}</span>
                  <div className="flex-1 h-7 bg-gray-100 rounded-md overflow-hidden">
                    <div
                      className="h-7 rounded-md flex items-center pl-2.5"
                      style={{ width: `${pct}%`, background: COLORS_MUNI[i % COLORS_MUNI.length] }}
                    >
                      <span className="text-[11px] font-medium text-white">{d.value.toLocaleString()}</span>
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 w-10 text-right">{pct}%</span>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Wealth Indicators */}
        <Card className="bg-white border border-gray-200 p-5 rounded-xl">
          <h3 className="font-syne text-sm font-bold text-gray-900 mb-1">Database Wealth Indicators</h3>
          <p className="text-xs text-gray-400 mb-4">Aggregate financial profile - government employed</p>
          <div className="grid grid-cols-2 gap-3">
            <WealthCard value="R16.1B" label="Annual gross payroll" color="#0D2C4E" />
            <WealthCard value="R10.4B" label="Annual disposable income" color="#1D9E75" />
            <WealthCard value="R371K" label="Avg disposable / member" color="#E8593C" />
            <WealthCard value="R31K" label="Avg disposable / month" color="#EF9F27" />
          </div>
          <div className="mt-4 p-3 bg-emerald-50 border-l-3 border-l-emerald-500 rounded-r-lg text-xs text-gray-500 leading-relaxed" style={{ borderLeft: '3px solid #1D9E75' }}>
            <strong className="text-emerald-700">Stable income base:</strong> Government employment offers guaranteed monthly salaries, UIF contributions, pension fund membership and medical aid - making this database extremely low-risk for financial services.
          </div>
        </Card>
      </div>

      {/* Top Workplaces */}
      {workplaceData.length > 0 && (
        <Card className="bg-white border border-gray-200 p-5 rounded-xl">
          <h3 className="font-syne text-sm font-bold text-gray-900 mb-1">Top Workplaces</h3>
          <p className="text-xs text-gray-400 mb-4">Member distribution by place of work</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={workplaceData.slice(0, 10)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(13,44,78,0.07)" />
              <XAxis type="number" tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: '#6b7280' }} width={140} />
              <Tooltip formatter={(v) => v.toLocaleString()} />
              <Bar dataKey="value" name="Members" fill="#1F4E79" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  );
};

const KpiCard = ({ value, label, sub, color, icon: Icon }) => (
  <Card className="bg-white border border-gray-200 p-4 rounded-xl relative overflow-hidden">
    <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: color }} />
    <div className="flex items-start justify-between">
      <div>
        <p className="font-syne text-xl font-bold text-gray-900">{value}</p>
        <p className="text-xs text-gray-500 mt-1">{label}</p>
        {sub && <p className="text-xs font-medium mt-0.5" style={{ color }}>{sub}</p>}
      </div>
      <Icon className="w-5 h-5 text-gray-300" />
    </div>
  </Card>
);

const WealthCard = ({ value, label, color }) => (
  <div className="bg-gray-50 rounded-lg p-3.5">
    <p className="font-syne text-lg font-bold" style={{ color }}>{value}</p>
    <p className="text-[10px] text-gray-400 mt-1">{label}</p>
  </div>
);
